import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirstChildWithClassName } from '../../../lib/window/Window';
import { FetchType } from '../../../types/api/FetchType';
import { isPersonalMessageList, PersonalMessageList } from '../../../types/api/PAL/PersonalMessageList';
import { BlockType, WidgetType } from '../../../types/api/ElementType';
import { isNotNull } from '../../../lib/typeGuards';
import { Namespace } from '../../../translation/namespace';
import { usePALMessages } from './usePALMessages';
import { hasWidgetIconId, hasWidgetTitle } from '../../../types/api/blocks/WidgetOverrides';
import { calculateNextElementsIndex, setElementFocus, setElementFocusById } from '../../../lib/DomUtils/utils';
import { PALPanelContext } from './PALPanelContext';
import { ScrollAreaContext } from '../../../components/atoms/ScrollArea/ScrollAreaContext';
import { PALPanel } from './PALPanel/PALPanel';
import { TabAnonymous, TabWithIcon } from './PALTab/tabs/Tabs';
import { PersonProfileImageSmall } from '../../../components/molecules/PersonProfileImageSmall';
import { Icons } from '../../../components/atoms/SVG';
import { PALTab } from './PALTab/PALTab';
import { accessKeys } from '../../../types/AccessKeys';
import { AppLibraryPersonalWidget } from '../../../_widgets/AppLibraryPersonal';
import { PersonalMessagesWidget } from '../../../_widgets/PersonalMessages';
import { UserAccountWidget } from '../../../_widgets/UserAccount';
import { UserAnonymousWidget } from '../../../_widgets/UserAnonymous';
import { FavoritedPagesWidget } from '../../../_widgets/FavoritedPages';
import { FavoritedUsersWidget } from '../../../_widgets/FavoritedUsers';
import { PersonalSettingsWidget } from '../../../_widgets/PersonalSettings';
import { WatchedPageMessagesWidget } from '../../../_widgets/WatchedPageMessages';
import { CustomPersonalMessagesWidget } from '../../../_widgets/CustomPersonalMessages';
import { ProfileImageSmall } from '../../../types/api/ImageType';
import { KeyEvent } from '../../../types/KeyEvent';

export type PALWidget =
  | UserAnonymousWidget
  | (UserAccountWidget & {
    image: ProfileImageSmall;
  })
  | WatchedPageMessagesWidget
  | CustomPersonalMessagesWidget
  | FavoritedPagesWidget
  | FavoritedUsersWidget
  | PersonalSettingsWidget
  | PersonalMessagesWidget
  | AppLibraryPersonalWidget;

type PersonalAccessLibraryProps = {
  widgets: PALWidget[];
  togglePAL: () => void;
  isExpanded: boolean;
  accessKeysEnabled: boolean;
};

function scrollPanel(activePanelId: string) {
  requestAnimationFrame(() => {
    const activePanelNode = getFirstChildWithClassName(activePanelId, 'scroll-area');
    if (activePanelNode) {
      activePanelNode.scrollTop = 0;
    }
  });
}

function computeIdByWidget(widget: PALWidget) {
  return `${widget.elementType}-${widget.uuid}`;
}

function computePanelId(widget: PALWidget) {
  return `pal-panel-${computeIdByWidget(widget)}`;
}

function computePalTabId(widget: PALWidget) {
  return `pal-tab-${computeIdByWidget(widget)}`;
}

/**
 * Retrieving all Widgets that need to be handled by the usePALMessages hook.
 */
const tryGetPersonalMessageList = (widget: PALWidget): PersonalMessageList | null => {
  const [block] = widget.blocks;
  if (block.fetchType === FetchType.instant) {
    if (isPersonalMessageList(block.embedded)) {
      return block.embedded;
    }
    if (block.elementType === BlockType.watchedpagemessagesblock) {
      return block.embedded.views.default.embedded;
    }
  }
  return null;
};

const getMessageCategoryFromWidget = (widget: PALWidget) => tryGetPersonalMessageList(widget)?.messageCategory;

const getAllMessageCategories = (widgets: PALWidget[]): PersonalMessageList[] => {
  return widgets.map(tryGetPersonalMessageList).filter(isNotNull);
};

export function PersonalAccessLibrary({
  widgets,
  togglePAL,
  isExpanded,
  accessKeysEnabled,
}: PersonalAccessLibraryProps) {
  const { t } = useTranslation(Namespace.PersonalAccessLibrary);
  const [activePanelId, setActivePanelId] = useState(computePanelId(widgets[0]));
  const { markMessageCategoryAsRead, hasUnreadMessages, messagesStates } = usePALMessages(
    getAllMessageCategories(widgets),
  );
  const [transitionEnded, setTransitionEnded] = useState<boolean>(false);
  const keyboardKeyKeyDown = useRef<boolean>(false);
  const tabBoxTabList = useRef<HTMLDivElement | null>(null);

  /**
   * Changing width of PAL, when scrollbar is needed (viewport lower than height of all buttons)
   * => scrollbar is visible and need 10px more space, see SON-19031
   */
  useEffect(() => {
    const container = tabBoxTabList.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { clientHeight, scrollHeight } = entry.target;
        const needsScrollbar = scrollHeight > clientHeight;
        // switching between 60px and 50px (scrollbar width)
        document.documentElement.style.setProperty('--pal-buttons-width', needsScrollbar ? '3.75rem' : '3.125rem');
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  /**
   * Toggles the Panel.
   * if a Tab has unread messages, a click should not close the panel, only remove the bubble/notification.   *
   */
  const togglePanel = (panelId: string, messageCategory?: string) => {
    if (panelId === activePanelId || !isExpanded) {
      if (!hasUnreadMessages(messageCategory)) {
        togglePAL();
      } else if (!isExpanded) {
        togglePAL();
      }
    }
  };

  // toggles PAL when open and in specific breakpoint
  const onTabClick =
    (messageCategory?: string) =>
      ({ panelId }: { panelId: string }) => {
        setActivePanelId(panelId);
        markMessageCategoryAsRead(messageCategory);
        scrollPanel(panelId);
        togglePanel(panelId, messageCategory);
      };

  const getTitlePostFix = ({ blocks: [block] }: PALWidget): string => {
    switch (block.elementType) {
      case BlockType.useraccount:
        return ` ${block.embedded.profileName}`;
      default:
        return '';
    }
  };

  const getTitle = (widget: PALWidget): string => {
    const {
      blocks: [block],
      elementType,
    } = widget;

    if (block.fetchType === FetchType.instant && hasWidgetTitle(widget)) {
      return widget.title;
    }

    return t(`${Namespace.Widget}:header.${elementType}`) + getTitlePostFix(widget);
  };

  const handleFocus = ({ panelId }: { panelId: string }) => {
    if (keyboardKeyKeyDown.current === true) {
      keyboardKeyKeyDown.current = false;
      setActivePanelId(panelId);
    }
  };

  const keyboardNavigation = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.code === KeyEvent.Home) {
      event.preventDefault();
      keyboardKeyKeyDown.current = true;
      if (event.target instanceof HTMLButtonElement) {
        setElementFocus(tabBoxTabList.current, 0, event.target.className);
      }
    }

    if (event.code === KeyEvent.End) {
      event.preventDefault();
      keyboardKeyKeyDown.current = true;
      if (isNotNull(tabBoxTabList.current) && event.target instanceof HTMLButtonElement) {
        setElementFocus(tabBoxTabList.current, tabBoxTabList.current.children.length - 1, event.target.className);
      }
    }

    if (event.code === KeyEvent.ArrowUp || event.code === KeyEvent.ArrowDown) {
      event.preventDefault();

      keyboardKeyKeyDown.current = true;

      if (event.target instanceof HTMLButtonElement) {
        setElementFocus(
          tabBoxTabList.current,
          calculateNextElementsIndex(
            tabBoxTabList.current,
            event.target.id,
            event.code === KeyEvent.ArrowDown ? 1 : -1,
          ),
          event.target.className,
        );
      }
    }
  };

  function renderWidget(widget: PALWidget) {
    switch (widget.elementType) {
      case WidgetType.useranonymous:
        return <UserAnonymousWidget {...widget} />;
      case WidgetType.useraccount:
        return <UserAccountWidget {...widget} />;
      case WidgetType.teamroommessages:
      case WidgetType.editorialMessages:
      case WidgetType.recommendationmessages:
        return <PersonalMessagesWidget {...widget} />;
      case WidgetType.custompersonalmessages:
        return <CustomPersonalMessagesWidget {...widget} />;
      case WidgetType.watchedpagemessagesblock:
        return <WatchedPageMessagesWidget {...widget} />;
      case WidgetType.favoritedpages:
        return <FavoritedPagesWidget {...widget} />;
      case WidgetType.favoritedusers:
        return <FavoritedUsersWidget {...widget} />;
      case WidgetType.personalsettings:
        return <PersonalSettingsWidget {...widget} />;
      case WidgetType.applibrarypersonal:
        return <AppLibraryPersonalWidget {...widget} />;
      default:
        return null;
    }
  }

  const createPanel = (widget: PALWidget) => {
    const panelId = computePanelId(widget);
    const correspondingPalTabId = computePalTabId(widget);

    return (
      <PALPanelContext.Provider
        key={widget.uuid}
        value={{
          isActivePanel: panelId === activePanelId,
          panelAreaExpanded: isExpanded,
          messagesStates,
        }}
      >
        <ScrollAreaContext.Provider
          value={{
            visible: panelId === activePanelId && isExpanded && transitionEnded,
          }}
        >
          <PALPanel
            key={panelId}
            id={panelId}
            isActivePanel={panelId === activePanelId}
            labelledby={correspondingPalTabId}
            panelAreaExpanded={isExpanded}
            focusTab={() => {
              setElementFocusById(tabBoxTabList.current, correspondingPalTabId);
            }}
          >
            {renderWidget(widget)}
          </PALPanel>
        </ScrollAreaContext.Provider>
      </PALPanelContext.Provider>
    );
  };

  const hasBadge = (widget: PALWidget) => {
    const messageCategory = getMessageCategoryFromWidget(widget);
    return hasUnreadMessages(messageCategory) ? t('newEntriesAvailable') : null;
  };
  const renderTabWithIcon = (widget: PALWidget) => {
    switch (widget.elementType) {
      case WidgetType.useranonymous:
        return <TabAnonymous />;
      case WidgetType.useraccount:
        return (
          <PersonProfileImageSmall
            modifier="personal"
            emptyAltText
            profileImageUrl={widget.image}
            profileName={widget.blocks[0].embedded.profileName}
          />
        );
      case WidgetType.teamroommessages:
        return <TabWithIcon icon={Icons.TEAMROOM_PEOPLE} badge={hasBadge(widget)} />;
      case WidgetType.editorialMessages:
        return <TabWithIcon icon={Icons.EDIT_PENCIL} badge={hasBadge(widget)} />;
      case WidgetType.recommendationmessages:
        return <TabWithIcon icon={Icons.RECOMMEND_PAPERPLANE} badge={hasBadge(widget)} />;
      case WidgetType.custompersonalmessages:
        return (
          <TabWithIcon icon={hasWidgetIconId(widget) ? widget.iconId : Icons.MAIL_ENVELOPE} badge={hasBadge(widget)} />
        );
      case WidgetType.watchedpagemessagesblock:
        return <TabWithIcon icon={Icons.OBSERVE_EYE} badge={hasBadge(widget)} />;
      case WidgetType.favoritedpages:
        return <TabWithIcon icon={Icons.FAVOURITE_HEART} badge={hasBadge(widget)} />;
      case WidgetType.favoritedusers:
        return <TabWithIcon icon={Icons.CONTACTS_PHONEBOOK} badge={hasBadge(widget)} />;
      case WidgetType.personalsettings:
        return <TabWithIcon icon={Icons.SETTINGS} badge={hasBadge(widget)} />;
      case WidgetType.applibrarypersonal:
        return <TabWithIcon icon={Icons.APP_LIBRARY} badge={null} />;
      default:
        return <TabWithIcon icon={Icons.INTERFACES} badge={hasBadge(widget)} />;
    }
  };

  const createButton = (widget: PALWidget) => {
    const dependentPanelId = computePanelId(widget);
    const palTabId = computePalTabId(widget);
    const messageCategory = getMessageCategoryFromWidget(widget);
    return (
      <PALTab
        key={palTabId}
        id={palTabId}
        title={getTitle(widget)}
        dependentPanelId={dependentPanelId}
        activePanelId={activePanelId}
        panelAreaExpanded={isExpanded}
        onClick={onTabClick(messageCategory)}
        onKeyUp={keyboardNavigation}
        onFocus={handleFocus}
        accessKey={accessKeysEnabled && widget.elementType === WidgetType.useraccount ? accessKeys.pal : undefined}
      >
        {renderTabWithIcon(widget)}
      </PALTab>
    );
  };
  return (
    <>
      <h2 className="invisible" id="personal-access-library-header">
        {t('personalAccessLibrary')}
      </h2>
      <div className="pal">
        <div className="pal__list-container" id="pal-container">
          <div ref={tabBoxTabList} className="pal__list" role="tablist" aria-orientation="vertical">
            {widgets.map(createButton)}
          </div>
        </div>
        <div
          className="pal__tabpanels"
          onTransitionEnd={() => {
            // integrating transition into render lifecycle (SIBZIEL-6901)
            setTransitionEnded(isExpanded);
          }}
        >
          {widgets.map(createPanel)}
        </div>
      </div>
    </>
  );
}
